// src/pages/SalesRecord/components/SalesRecordTable.js
import React, { useEffect, useState } from "react";
import { Table, Button, Tooltip, Tag } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LockOutlined,
  PrinterOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { salesOrderDeleteApi } from "../../../../../apiservice/sales";
import {
  deleteSalesOrder,
  fetchSalesOrderRecord,
} from "../SalesOrderRecordThunk";
import { formatToCompanyTimezone } from "../../../../../lib/functions";
import { useIsAdminUser } from "../../../../../lib/accessList";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import { useTxLock } from "../../../../../hooks/useTxLock";
import VoucherPdfActions from "../../../../../components/common/VoucherPdfActions";

const SalesOrderRecordTable = ({ printMode = false }) => {
  const { list, loading, filters } = useSelector((state) => state.salesOrderRecord);
  const authInfo = useSelector(selectAuthInfo);
  console.log('Auth Info:', authInfo);
  console.log('Auth Info: res',  authInfo?.company_type === "Pharmacy" || authInfo?.company_type === "Drug Store");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  // const [isAdminUser, isAdminUserSet] = useState(false);

  // useEffect(() => {
  //   const hrRoles = [
  //     // Admin role
  //     "ForthERP_Basic_admin",
  //     "ForthERP_Standard_admin",
  //     "ForthERP_Pro_admin",
  //     "PharmaERP_Basic_admin",
  //     "PharmaERP_Standard_admin",
  //     "PharmaERP_Pro_admin",
  //     "PharmaBooks_Basic_admin",
  //     "PharmaBooks_Standard_admin",
  //     "PharmaBooks_Pro_admin",
  //     "ForthBooks_Basic_admin",
  //     "ForthBooks_Standard_admin",
  //     "ForthBooks_Pro_admin",
  //     "HRPayroll_Standard_admin",
  //     "HRPayroll_Pro_admin",
  //     "SuperERP_admin",
  //     "SuperERP_super_admin",
  //   ];

  //   const userRoles = authInfo.userInfo.user_role_names
  //     ? authInfo.userInfo.user_role_names.split(",").map((role) => role.trim())
  //     : [];

  //   const isAdminHR = userRoles.some((role) => hrRoles.includes(role));

  //   isAdminUserSet(isAdminHR);
  // }, [authInfo]);

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  // -----------------------
  // Columns for WITHOUT ITEM (summary)
  // -----------------------
  const summaryColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Voucher No", dataIndex: "vch_no" },
    {
      title: "Date", dataIndex: "date", render: (v) =>
        v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Customer / Debtor", dataIndex: "customer" },
    // { title: "Employee", dataIndex: "employee" },
    // { title: "Mode of Pay", dataIndex: "mode" },
    { title: "Created By", dataIndex: "full_name" },
    {
      title: "Sub Total",
      dataIndex: "subtotal",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      render: (val) => Number(val).toFixed(2),
    },
    showTax && {
      title: taxLabel,
      dataIndex: "vat",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Service",
      dataIndex: "service",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Total",
      dataIndex: "total",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status, record) => {
        switch (status) {
          case "Approved":
            return <Tag color="green">Approved ✅</Tag>;

          case "Pending":
            // If partially delivered (remaining qty > 0 but not full)
            if (
              Number(record.remainingQty) > 0 &&
              Number(record.remainingQty) < Number(record.totalQty)
            ) {
              return <Tag color="orange">Partially Delivered ⏳</Tag>;
            }
            return <Tag color="blue">Pending ⏳</Tag>;

          case "Cancelled":
            return <Tag color="red">Cancelled ❌</Tag>;

          default:
            return <Tag color="default">{status}</Tag>;
        }
      },
    },
    {
      // Separate from "Status" above (open vs. converted) — this is the
      // discount-approval workflow's own state, read directly off the
      // approval_status column.
      title: "Approval",
      dataIndex: "approval_status",
      render: (approvalStatus) => {
        if (approvalStatus === "pending") return <Tag color="gold">Pending Approval</Tag>;
        if (approvalStatus === "approved") return <Tag color="green">Approved</Tag>;
        return null;
      },
    },

    // {
    //   title: "Paid",
    //   dataIndex: "paid",
    //   render: (val) => Number(val).toFixed(2),
    // },
    // {
    //   title: "Due",
    //   dataIndex: "due",
    //   render: (val) => Number(val).toFixed(2),
    // },
    !printMode && {
      title: "Actions",
      className: "no-print",
      render: (_, record) => {

        const txLocked = isTxLocked(record?.date);
        const handleDelete = async () => {
          if (record.status === "Approved") {
            Swal.fire(
              "Not Allowed",
              "Approved orders cannot be deleted",
              "warning",
            );
            return;
          }
          if (!isAdminUser) {
            Swal.fire("Not Allowed", "Only Admin can delete orders", "warning");
            return;
          }

          const result = await Swal.fire({
            title: "Are you sure?",
            text: "Sales Order will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Delete",
          });

          if (!result.isConfirmed) return;

          const res = await dispatch(deleteSalesOrder(record.id));

          if (res.meta.requestStatus === "fulfilled") {
            Swal.fire(
              "Deleted!",
              "Sales order deleted successfully",
              "success",
            );

            // OPTIONAL: refetch with filters (only if backend logic changes data)
            dispatch(
              fetchSalesOrderRecord({
                selectedFilterType: filters.filterType,
                selectedRecordType: filters.recordType,
                filters,
              }),
            );
          }
        };

        return (
          <div
            style={{ display: "flex", gap: "8px", justifyContent: "center" }}
          >
            {/* VIEW VOUCHER */}
            <Tooltip title={record.approval_status === "pending" ? "Pending approval — cannot view yet" : "View Voucher"}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                disabled={record.approval_status === "pending"}
                onClick={() => {
                  if (record.approval_status === "pending") return;
                  navigate(`/order/quick-sales-order-voucher/${record.id}`);
                }}
              />
            </Tooltip>

            {/* CONVERT TO SALES */}
            {record.status !== "Approved" ? (
          <Tooltip
  title={
    record.approval_status === "pending"
      ? "Pending approval — cannot convert yet"
      : txLocked
      ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
      : "Convert to Sales"
  }
>
  <Button
    type="link"
    icon={<SwapOutlined />}
    disabled={txLocked || record.approval_status === "pending"}
    onClick={() => {
      if (txLocked || record.approval_status === "pending") return;
      navigate(
        authInfo?.company_type === "Pharmacy"
          ? `/sales/pharma-sales?convert_from_sale_order=${record.id}`
          : `/invoice?convert_from_sale_order=${record.id}`
      );
    }}
  />
</Tooltip>
            ) : (
              /* 🔒 Show Lock icon when conversion is blocked */
              <Tooltip title="Order fully delivered - Conversion locked">
                <LockOutlined
                  style={{ fontSize: 18, color: "#bfbfbf", marginLeft: 8 }}
                />
              </Tooltip>
            )}

            {/* EDIT (if Pending + user allowed) */}
            {/* {record.status !== "Approved" && accessType === "WRITE" && (
          <Tooltip title="Edit Order">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/order/sales-order-update/${record.id}`)}
            />
          </Tooltip>
        )} */}

            {record.status !== "Approved" && !record.hasDeliveredQty && (
      <Tooltip
  title={
    txLocked
      ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
      : "Edit Order"
  }
>
  <Button
    type="link"
    icon={<EditOutlined />}
    disabled={!(isAdminUser || canEdit) || txLocked}
    onClick={() =>
      navigate(`/order/sales-order-entry/${record.id}?tab=entry`)
    }
  />
</Tooltip>
            )}

            {record.hasDeliveredQty && (
              <Tooltip title="Cannot edit - Items already delivered">
                <LockOutlined style={{ fontSize: 18, color: "#bfbfbf" }} />
              </Tooltip>
            )}



            {/* DELETE (if Pending + user allowed) */}
            {record.status !== "Approved" && !record.hasDeliveredQty && (
    <Tooltip
  title={
    txLocked
      ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
      : "Delete Order"
  }
>
  <Button
    type="link"
    danger
    disabled={
      !(record.status !== "Approved" && (isAdminUser || canDelete)) ||
      txLocked
    }
    icon={<DeleteOutlined />}
    onClick={handleDelete}
  />
</Tooltip>
            )}

            <VoucherPdfActions
              url={`/print/sales-order/${record.id}`}
              fileName={`SalesOrder_${record.vch_no}.pdf`}
              disabled={record.approval_status === "pending"}
              disabledTitle="Pending approval — cannot share or download yet"
            />
          </div>
        );
      }

    },
  ].filter(Boolean);

  // -----------------------
  // Columns for WITH ITEM (detailed)
  // -----------------------
  const withItemColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH No", dataIndex: "vch_no" },
    {
      title: "Date", dataIndex: "date", render: (v) =>
        v ? formatToCompanyTimezone(v).split(" ")[0] : "-",
    },
    { title: "Customer / Debtor", dataIndex: "customer" },
    // { title: "Employee", dataIndex: "employee" },
    { title: "Created By", dataIndex: "full_name" },
    // { title: "Mode of Pay", dataIndex: "mode" },
    // { title: "Item", dataIndex: "item" },
    {
      title: "Items",
      render: (_, record) => record.items?.length || 0,
    },
    // { title: "Rate", dataIndex: "rate" },
    // Only show Discount column if at least one row has discount
    ...(list.some((row) => row.discount && row.discount !== "0%")
      ? [{ title: "Discount%", dataIndex: "discount" }]
      : []),
    // Only show VAT column if at least one row has vat
    ...(showTax && list.some((row) => row.vat && row.vat !== "0%")
      ? [{ title: "VAT%", dataIndex: "vat" }]
      : []),
    // { title: "Qty", dataIndex: "qty" },
    // { title: "Total", dataIndex: "total" },
    {
      title: "Total",
      dataIndex: "total",
      render: (val) => Number(val).toFixed(2),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status, record) => {
        switch (status) {
          case "Approved":
            return <Tag color="green">Approved ✅</Tag>;

          case "Pending":
            // If partially delivered (remaining qty > 0 but not full)
            if (
              Number(record.remainingQty) > 0 &&
              Number(record.remainingQty) < Number(record.totalQty)
            ) {
              return <Tag color="orange">Partially Delivered ⏳</Tag>;
            }
            return <Tag color="blue">Pending ⏳</Tag>;

          case "Cancelled":
            return <Tag color="red">Cancelled ❌</Tag>;

          default:
            return <Tag color="default">{status}</Tag>;
        }
      },
    },
    {
      // Separate from "Status" above (open vs. converted) — this is the
      // discount-approval workflow's own state, read directly off the
      // approval_status column.
      title: "Approval",
      dataIndex: "approval_status",
      render: (approvalStatus) => {
        if (approvalStatus === "pending") return <Tag color="gold">Pending Approval</Tag>;
        if (approvalStatus === "approved") return <Tag color="green">Approved</Tag>;
        return null;
      },
    },
    !printMode && {
      title: "Actions",
      className: "no-print",
      render: (_, record) => {
        const handleDelete = async () => {
          const result = await Swal.fire({
            title: "Are you sure?",
            text: "Order will be deleted!",
            icon: "warning",
            showCancelButton: true,
          });
          if (!result.isConfirmed) return;

          await salesOrderDeleteApi({ sale_o_id: record.saleOrderId });
          dispatch(fetchSalesOrderRecord(filters));
        };

        return (
          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
            {/* VIEW VOUCHER */}
            <Tooltip title={record.approval_status === "pending" ? "Pending approval — cannot view yet" : "View Voucher"}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                disabled={record.approval_status === "pending"}
                onClick={() => {
                  if (record.approval_status === "pending") return;
                  navigate(
                    `/order/quick-sales-order-voucher/${record.saleOrderId}`,
                  );
                }}
              />
            </Tooltip>

            {/* CONVERT TO SALES */}
            {record.status !== "Approved" && record.hasRemainingQty ? (
              <Tooltip title={record.approval_status === "pending" ? "Pending approval — cannot convert yet" : "Convert to Sales"}>
                <Button
                  type="link"
                  icon={<SwapOutlined />}
                  disabled={record.approval_status === "pending"}
              onClick={() => {
  if (record.approval_status === "pending") return;
  navigate(
    authInfo?.company_type === "Pharmacy" || authInfo?.company_type === "Drug Store"
      ? `/sales/pharma-sales?convert_from_sale_order=${record.id}`
      : `/invoice?convert_from_sale_order=${record.id}`
  );
}}
                />
              </Tooltip>
            ) : (
              /* 🔒 Show Lock icon when conversion is blocked */
              <Tooltip title="Order fully delivered - Conversion locked">
                <LockOutlined
                  style={{ fontSize: 18, color: "#bfbfbf", marginLeft: 8 }}
                />
              </Tooltip>
            )}

            {record.status !== "Approved" && !record.hasDeliveredQty && (
              <Tooltip title="Edit Order">
                <Button
                  type="link"
                  disabled={!(isAdminUser || canEdit)}
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(`/order/sales-order-entry/${record.saleOrderId}?tab=entry`)
                  }
                />
              </Tooltip>
            )}

            {record.hasDeliveredQty && (
              <Tooltip title="Cannot edit - Items already delivered">
                <LockOutlined style={{ fontSize: 18, color: "#bfbfbf" }} />
              </Tooltip>
            )}

            {/* DELETE (if Pending + user allowed) */}
            {record.status !== "Approved" && !record.hasDeliveredQty  && (
              <Tooltip title="Delete Order">
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                  disabled={!(record.status !== "Approved" && (isAdminUser || canDelete))}
                />
              </Tooltip>
            )}

            {/* EDIT (if Pending + user allowed) */}
            {/* {record.status !== "Approved" && accessType === "WRITE" && (
          <Tooltip title="Edit Order">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => navigate(`/order/sales-order-update/${record.saleOrderId}`)}
            />
          </Tooltip>
        )} */}

            {/* DELETE (if Pending + user allowed) */}
            {/* {record.status !== "Approved" && accessType === "WRITE" && (
          <Tooltip title="Delete">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            />
          </Tooltip>
        )} */}
          </div>
        );
      }

    },
  ].filter(Boolean);

  // -----------------------
  // Grand Totals
  // -----------------------
  let totals;

  if (filters.recordType === "with item") {
    totals = list.reduce(
      (acc, row) => {
        acc.total += Number(row.total || 0);
        acc.master_qty += Number(row.master_qty || 0);
        acc.base_qty += Number(row.base_qty || 0);
        return acc;
      },
      { total: 0, master_qty: 0, base_qty: 0 },
    );
  } else {
    totals = list.reduce(
      (acc, row) => {
        acc.subtotal += Number(row.subtotal || 0);
        acc.discount += Number(row.discount || 0);
        acc.vat += Number(row.vat || 0);
        acc.service += Number(row.service || 0);
        acc.total += Number(row.total || 0);
        acc.paid += Number(row.paid || 0);
        acc.due += Number(row.due || 0);
        return acc;
      },
      {
        subtotal: 0,
        discount: 0,
        vat: 0,
        service: 0,
        total: 0,
        paid: 0,
        due: 0,
      },
    );
  }

  // -----------------------
  // Expandable sub-row for WITH ITEM
  // -----------------------
  const expandable =
    filters.recordType === "with item"
      ? {
        expandedRowRender: (record) => (
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={record.items}
            scroll={{ x: "max-content" }}
            columns={[
              { title: "Item", dataIndex: "item" },
              { title: "Rate", dataIndex: "rate" },
              ...(record.items.some(i => i.discount)
                ? [{ title: "Discount%", dataIndex: "discount" }]
                : []),
              ...(record.items.some(i => i.vat)
                ? [{ title: "VAT%", dataIndex: "vat" }]
                : []),
              { title: "Qty", dataIndex: "qty" },
              {
                title: "Total",
                dataIndex: "total",
                render: (v) => Number(v).toFixed(2),
              },
            ]}
          />
        ),
        rowExpandable: (record) => record.items?.length > 0,
      }
      : undefined;


  return (
    <Table
      rowKey="id"
      data-testid="sales-order-record-table"
      columns={
        filters.recordType === "with item" ? withItemColumns : summaryColumns
      }
      dataSource={list}
      loading={loading}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
      }}
      onChange={handleTableChange}

      bordered
      expandable={expandable}
      scroll={printMode ? undefined : { x: "max-content" }}
      size={printMode ? "small" : "middle"}
      summary={() => {
        if (filters.recordType === "with item") {
          // 👇 Calculate grand totals for with item
          const grand = list.reduce(
            (acc, row) => {
              acc.masterQty += row.masterQty || 0;
              acc.baseQty += row.baseQty || 0;
              acc.total += Number(row.total || 0);
              return acc;
            },
            { masterQty: 0, baseQty: 0, total: 0 },
          );

          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={10}>
                <strong>
                  Grand Total : Master U. Qty = {grand.masterQty}, Base U. Qty ={" "}
                  {grand.baseQty}
                </strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10}>
                <strong>{grand.total.toFixed(2)}</strong>
              </Table.Summary.Cell>
              {!printMode && (
                <Table.Summary.Cell index={12}></Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          );
        } else {
          // keep your existing "without item" summary
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <strong>Grand Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}>
                {totals.subtotal.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7}>
                {totals.discount.toFixed(2)}
              </Table.Summary.Cell>
              {showTax && (
                <Table.Summary.Cell index={8}>
                  {totals.vat.toFixed(2)}
                </Table.Summary.Cell>
              )}
              <Table.Summary.Cell index={showTax ? 9 : 8}>
                {totals.service.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={showTax ? 10 : 9}>
                {totals.total.toFixed(2)}
              </Table.Summary.Cell>
              {/* <Table.Summary.Cell index={11}>
                {totals.paid.toFixed(2)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={12}>
                {totals.due.toFixed(2)}
              </Table.Summary.Cell> */}
              {!printMode && (
                <Table.Summary.Cell index={showTax ? 13 : 12}></Table.Summary.Cell>
              )}
            </Table.Summary.Row>
          );
        }
      }}
    />
  );
};

export default SalesOrderRecordTable;
